import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { FONTS } from '@/styles';
import { NutritionalInfo } from '@/contexts/item-context-types';
import { NutritionLabelNutrientRow } from './nutrition-label-nutrient-row';
import { Text, View } from 'react-native';

interface Props {
  info: NutritionalInfo;
  tabular?: boolean;
}

// Label formats for vertical and tabular display are based on FDA regulations:
// https://www.fda.gov/media/99151/download
export const NutritionLabel: React.FC<Props> = ({ info, tabular }) => {
  const [styles] = useStyles();
  const bulletedList = useMemo(() => {
    if (!tabular) {
      return null;
    }
    const listItems: string[] = [];
    listItems.push(
      `${i18n.t('nutrition.vitamin_d')} ${info.vitamin_d.amount} ${
        info.vitamin_d.percent_dv ?? ''
      }%`,
    );
    listItems.push(
      `${i18n.t('nutrition.calcium')} ${info.calcium.amount} ${
        info.calcium.percent_dv ?? ''
      }%`,
    );
    listItems.push(
      `${i18n.t('nutrition.iron')} ${info.iron.amount} ${
        info.iron.percent_dv ?? ''
      }%`,
    );
    listItems.push(
      `${i18n.t('nutrition.potassium')} ${info.potassium.amount} ${
        info.potassium.percent_dv ?? ''
      }%`,
    );
    return listItems.join(' • ');
  }, [info, tabular]);

  if (tabular) {
    const tabularDailyValueHeader = (
      <View style={styles.tabularDailyValueHeader}>
        <Text style={styles.tabularDailyValueText}>
          {i18n.t('nutrition.amount_slash_serving')}
        </Text>
        <Text style={styles.tabularDailyValueText}>
          {i18n.t('nutrition.percent_daily_value')}
        </Text>
      </View>
    );
    return (
      <View style={styles.tabularBase}>
        <View style={styles.tabularColumnPrimary}>
          <Text style={[styles.headerText, styles.tabularHeaderText]}>
            {i18n.t('nutrition.nutrition_facts')}
          </Text>
          <View style={styles.tabularServingsContainer}>
            <Text style={styles.servingsPerContainerText}>
              {i18n.t('nutrition.servings_per_container', {
                count: info.servings_per_container,
              })}
            </Text>
            <Text style={styles.servingSizeText}>
              {i18n.t('nutrition.serving_size')}
            </Text>
            <Text style={styles.servingSizeText}>{info.serving_size}</Text>
          </View>
          <View style={styles.tabularCaloriesContainer}>
            <View collapsable={false}>
              <Text style={styles.caloriesText}>
                {i18n.t('nutrition.calories')}
              </Text>
              <Text style={styles.servingSizeText}>
                {i18n.t('nutrition.per_serving')}
              </Text>
            </View>
            <Text style={styles.caloriesAmountText}>{info.calories}</Text>
          </View>
        </View>
        <View style={styles.tabularColumnMiddle}>
          <View style={styles.tabularTopMiddle}>
            <View style={styles.tabularMiddleSection}>
              {tabularDailyValueHeader}
              <NutritionLabelNutrientRow
                amount={info.total_fat.amount}
                emphasizeName
                emphasizePercentDailyValue
                name={i18n.t('nutrition.total_fat')}
                percentDailyValue={info.total_fat.percent_dv}
                unitName={info.total_fat.unit_name}
              />
              <NutritionLabelNutrientRow
                amount={info.saturated_fat.amount}
                containerStyle={styles.nutrientRowIndent}
                emphasizePercentDailyValue
                name={i18n.t('nutrition.saturated_fat')}
                percentDailyValue={info.saturated_fat.percent_dv}
                unitName={info.saturated_fat.unit_name}
              />
              <NutritionLabelNutrientRow
                amount={info.trans_fat.amount}
                containerStyle={styles.nutrientRowIndent}
                emphasizePercentDailyValue
                name={i18n.t('nutrition.trans_fat')}
                percentDailyValue={info.trans_fat.percent_dv}
                unitName={info.trans_fat.unit_name}
              />
              <NutritionLabelNutrientRow
                amount={info.cholesterol.amount}
                emphasizeName
                emphasizePercentDailyValue
                name={i18n.t('nutrition.cholesterol')}
                percentDailyValue={info.cholesterol.percent_dv}
                unitName={info.cholesterol.unit_name}
              />
              <NutritionLabelNutrientRow
                amount={info.sodium.amount}
                containerStyle={styles.tabularLastNutrientRow}
                emphasizeName
                emphasizePercentDailyValue
                name={i18n.t('nutrition.sodium')}
                percentDailyValue={info.sodium.percent_dv}
                unitName={info.sodium.unit_name}
              />
            </View>
            <View style={styles.tabularMiddleSection}>
              {tabularDailyValueHeader}
              <NutritionLabelNutrientRow
                amount={info.total_carbohydrate.amount}
                emphasizeName
                emphasizePercentDailyValue
                name={i18n.t('nutrition.total_carbohydrate')}
                percentDailyValue={info.total_carbohydrate.percent_dv}
                unitName={info.total_carbohydrate.unit_name}
              />
              <NutritionLabelNutrientRow
                amount={info.dietary_fiber.amount}
                containerStyle={styles.nutrientRowIndent}
                emphasizePercentDailyValue
                name={i18n.t('nutrition.dietary_fiber')}
                percentDailyValue={info.dietary_fiber.percent_dv}
                unitName={info.dietary_fiber.unit_name}
              />
              <NutritionLabelNutrientRow
                amount={info.total_sugars.amount}
                containerStyle={styles.nutrientRowIndent}
                emphasizePercentDailyValue
                name={i18n.t('nutrition.total_sugars')}
                percentDailyValue={info.total_sugars.percent_dv}
                unitName={info.total_sugars.unit_name}
              />
              <NutritionLabelNutrientRow
                containerStyle={styles.nutrientRowAddedSugars}
                emphasizePercentDailyValue
                name={i18n.t('nutrition.added_sugars', {
                  amount: info.added_sugars?.amount ?? '0g',
                })}
                percentDailyValue={info.added_sugars?.percent_dv ?? 0}
                unitName={info.added_sugars?.unit_name ?? ''}
              />
              <NutritionLabelNutrientRow
                amount={info.protein.amount}
                containerStyle={styles.tabularLastNutrientRow}
                emphasizeName
                emphasizePercentDailyValue
                name={i18n.t('nutrition.protein')}
                percentDailyValue={info.protein.percent_dv}
                unitName={info.protein.unit_name}
              />
            </View>
          </View>
          <View style={styles.tabularBottomMiddle}>
            <Text style={styles.tabularNutrientText}>{bulletedList}</Text>
          </View>
        </View>
        <View style={styles.tabularColumnFooter}>
          <Text style={styles.footerText}>
            {i18n.t('nutrition.nutrition_label_footer')}
          </Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.base}>
      <View style={styles.header}>
        <Text style={styles.headerText}>
          {i18n.t('nutrition.nutrition_facts')}
        </Text>
      </View>
      <View style={styles.servingsContainer}>
        <Text style={styles.servingsPerContainerText}>
          {i18n.t('nutrition.servings_per_container', {
            count: info.servings_per_container,
          })}
        </Text>
        <View style={styles.servingSizeContainer}>
          <Text style={styles.servingSizeText}>
            {i18n.t('nutrition.serving_size')}
          </Text>
          <Text style={styles.servingSizeText}>{info.serving_size}</Text>
        </View>
      </View>
      <View style={styles.caloriesContainer}>
        <View>
          <Text style={styles.caloriesAmountPerServingText}>
            {i18n.t('nutrition.amount_per_serving')}
          </Text>
          <Text style={styles.caloriesText}>
            {i18n.t('nutrition.calories')}
          </Text>
        </View>
        <Text style={styles.caloriesAmountText}>{info.calories}</Text>
      </View>
      <View style={styles.keyNutrientsContainer}>
        <NutritionLabelNutrientRow
          containerStyle={styles.nutrientRowPercentDailyValue}
          emphasizeName
          name={i18n.t('nutrition.percent_daily_value')}
        />
        <NutritionLabelNutrientRow
          amount={info.total_fat.amount}
          emphasizeName
          emphasizePercentDailyValue
          name={i18n.t('nutrition.total_fat')}
          percentDailyValue={info.total_fat.percent_dv}
          unitName={info.total_fat.unit_name}
        />
        <NutritionLabelNutrientRow
          amount={info.saturated_fat.amount}
          containerStyle={styles.nutrientRowIndent}
          emphasizePercentDailyValue
          name={i18n.t('nutrition.saturated_fat')}
          percentDailyValue={info.saturated_fat.percent_dv}
          unitName={info.saturated_fat.unit_name}
        />
        <NutritionLabelNutrientRow
          amount={info.trans_fat.amount}
          containerStyle={styles.nutrientRowIndent}
          emphasizePercentDailyValue
          name={i18n.t('nutrition.trans_fat')}
          percentDailyValue={info.trans_fat.percent_dv}
          unitName={info.trans_fat.unit_name}
        />
        <NutritionLabelNutrientRow
          amount={info.cholesterol.amount}
          emphasizeName
          emphasizePercentDailyValue
          name={i18n.t('nutrition.cholesterol')}
          percentDailyValue={info.cholesterol.percent_dv}
          unitName={info.cholesterol.unit_name}
        />
        <NutritionLabelNutrientRow
          amount={info.sodium.amount}
          emphasizeName
          emphasizePercentDailyValue
          name={i18n.t('nutrition.sodium')}
          percentDailyValue={info.sodium.percent_dv}
          unitName={info.sodium.unit_name}
        />
        <NutritionLabelNutrientRow
          amount={info.total_carbohydrate.amount}
          emphasizeName
          emphasizePercentDailyValue
          name={i18n.t('nutrition.total_carbohydrate')}
          percentDailyValue={info.total_carbohydrate.percent_dv}
          unitName={info.total_carbohydrate.unit_name}
        />
        <NutritionLabelNutrientRow
          amount={info.dietary_fiber.amount}
          containerStyle={styles.nutrientRowIndent}
          emphasizePercentDailyValue
          name={i18n.t('nutrition.dietary_fiber')}
          percentDailyValue={info.dietary_fiber.percent_dv}
          unitName={info.dietary_fiber.unit_name}
        />
        <NutritionLabelNutrientRow
          amount={info.total_sugars.amount}
          containerStyle={styles.nutrientRowIndent}
          emphasizePercentDailyValue
          name={i18n.t('nutrition.total_sugars')}
          percentDailyValue={info.total_sugars.percent_dv}
          unitName={info.total_sugars.unit_name}
        />
        <NutritionLabelNutrientRow
          containerStyle={styles.nutrientRowAddedSugars}
          emphasizePercentDailyValue
          name={i18n.t('nutrition.added_sugars', {
            amount: info.added_sugars?.amount ?? '0g',
          })}
          percentDailyValue={info.added_sugars?.percent_dv ?? 0}
          unitName={info.added_sugars?.unit_name ?? ''}
        />
        <NutritionLabelNutrientRow
          amount={info.protein.amount}
          emphasizeName
          emphasizePercentDailyValue
          name={i18n.t('nutrition.protein')}
          percentDailyValue={info.protein.percent_dv}
          unitName={info.protein.unit_name}
        />
      </View>
      <View style={styles.otherNutrientsContainer}>
        <NutritionLabelNutrientRow
          amount={info.vitamin_d.amount}
          name={i18n.t('nutrition.vitamin_d')}
          percentDailyValue={info.vitamin_d.percent_dv}
          unitName={info.vitamin_d.unit_name}
        />
        <NutritionLabelNutrientRow
          amount={info.calcium.amount}
          name={i18n.t('nutrition.calcium')}
          percentDailyValue={info.calcium.percent_dv}
          unitName={info.calcium.unit_name}
        />
        <NutritionLabelNutrientRow
          amount={info.iron.amount}
          name={i18n.t('nutrition.iron')}
          percentDailyValue={info.iron.percent_dv}
          unitName={info.iron.unit_name}
        />
        <NutritionLabelNutrientRow
          amount={info.potassium.amount}
          name={i18n.t('nutrition.potassium')}
          percentDailyValue={info.potassium.percent_dv}
          unitName={info.potassium.unit_name}
        />
      </View>
      <Text style={styles.footerText}>
        {i18n.t('nutrition.nutrition_label_footer')}
      </Text>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    borderColor: theme.colors.black,
    borderWidth: 1,
    marginLeft: 'auto',
    marginRight: 'auto',
    maxWidth: 320,
    padding: 4,
  },
  caloriesAmountPerServingText: {
    ...FONTS.helveticaBlack,
    fontSize: 12,
    lineHeight: 12,
  },
  caloriesAmountText: {
    ...FONTS.helveticaBlack,
    fontSize: 32,
    lineHeight: 32,
  },
  caloriesContainer: {
    alignItems: 'flex-end',
    borderBottomColor: theme.colors.black,
    borderBottomWidth: 4,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 4,
  },
  caloriesText: {
    ...FONTS.helveticaBlack,
    fontSize: 20,
    lineHeight: 20,
    marginTop: 2,
  },
  footerText: {
    ...FONTS.helvetica,
    fontSize: 10,
    lineHeight: 10,
    paddingTop: 4,
  },
  header: {
    borderBottomColor: theme.colors.black,
    borderBottomWidth: 1,
  },
  headerText: {
    ...FONTS.helveticaBlack,
    fontSize: 32,
    lineHeight: 32,
  },
  keyNutrientsContainer: {
    borderBottomColor: theme.colors.black,
    borderBottomWidth: 12,
  },
  nutrientRowAddedSugars: {
    paddingLeft: 32,
  },
  nutrientRowIndent: {
    paddingLeft: 16,
  },
  nutrientRowPercentDailyValue: {
    justifyContent: 'flex-end',
  },
  otherNutrientsContainer: {
    borderBottomColor: theme.colors.black,
    borderBottomWidth: 4,
  },
  servingsContainer: {
    borderBottomColor: theme.colors.black,
    borderBottomWidth: 12,
    paddingVertical: 4,
  },
  servingSizeContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  servingSizeText: {
    ...FONTS.helveticaBlack,
    fontSize: 14,
    lineHeight: 14,
  },
  servingsPerContainerText: {
    ...FONTS.helvetica,
    fontSize: 14,
    lineHeight: 14,
  },
  tabularBase: {
    borderColor: 'black',
    borderWidth: 1,
    flexDirection: 'row',
    gap: 12,
    padding: 4,
  },
  tabularBottomMiddle: {
    paddingTop: 4,
  },
  tabularCaloriesContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  tabularColumnFooter: {
    flex: 1,
    paddingHorizontal: 4,
  },
  tabularColumnMiddle: {
    flex: 6,
  },
  tabularColumnPrimary: {
    flex: 2,
  },
  tabularDailyValueHeader: {
    borderBottomColor: 'black',
    borderBottomWidth: 3,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingBottom: 2,
  },
  tabularDailyValueText: {
    ...FONTS.helveticaBlack,
    fontSize: 12,
    lineHeight: 12,
  },
  tabularHeaderText: {
    borderBottomColor: 'black',
    borderBottomWidth: 1,
    marginBottom: 4,
    paddingBottom: 4,
  },
  tabularLastNutrientRow: {
    borderBottomWidth: 0,
    paddingTop: 2,
    // reset paddingVertical from tabularMiddleSection, setting bottom to 0 does not work on web
    paddingVertical: 0,
  },
  tabularMiddleSection: {
    borderBottomColor: 'black',
    borderBottomWidth: 3,
    flex: 1,
  },
  tabularNutrientText: {
    ...FONTS.helvetica,
    fontSize: 14,
    lineHeight: 14,
  },
  tabularServingsContainer: {
    borderBottomColor: 'black',
    borderBottomWidth: 1,
    marginBottom: 4,
    paddingBottom: 4,
  },
  tabularTopMiddle: {
    flex: 1,
    flexDirection: 'row',
    gap: 8,
  },
}));
